import type { Table } from "./table";

export type Schema =
  | AnySchema
  | BooleanSchema
  | NumberSchema
  | StringSchema
  | NullishSchema
  | ArraySchema
  | ObjectSchema
  | UnionSchema
  | EnumSchema
  | TupleSchema
  | LiteralSchema
  | TypeIdentifierSchema
  | InterfaceSchema
  | PartialSchema
  | PickSchema
  | OmitSchema
  | ManualSchema
  | NominalNumberSchema
  | IntersectionSchema
  | MappedObjectSchema
  | ObjectPropertySchema
  | TypedArraySchema
  | TypeReferenceSchema
  | TypeParameterSchema
  | ForeignKeySchema
  | ForeignKeyPathSchema
  | ForeignKeyStringSchema
  | DeprecatedSchema
  | PrimaryKeySchema
  | PrimaryKeyTableSchema
  | ForeignKeyTableSchema
  | ForeignKeyHashTableSchema
  | ForeignKeyHashJoinTableSchema
  | MovedSchema
  | DeletedSchema
  | DescribedSchema;

export type SchemaKind = Schema["kind"];

export type ForeignKeySchemaType = Extract<
  SchemaKind,
  | "FOREIGN_KEY"
  | "FOREIGN_KEY_PATH"
  | "FOREIGN_KEY_STRING"
  | "FOREIGN_KEY_TABLE"
  | "FOREIGN_KEY_HASH_TABLE"
  | "FOREIGN_KEY_HASH_JOIN_TABLE"
>;

export function isForeignKeySchemaType(kind: SchemaKind): kind is ForeignKeySchemaType {
  return (
    kind === "FOREIGN_KEY" ||
    kind === "FOREIGN_KEY_PATH" ||
    kind === "FOREIGN_KEY_STRING" ||
    kind === "FOREIGN_KEY_TABLE" ||
    kind === "FOREIGN_KEY_HASH_TABLE" ||
    kind === "FOREIGN_KEY_HASH_JOIN_TABLE"
  );
}

export interface ForeignKeySchema {
  readonly kind: "FOREIGN_KEY";
  readonly type: string[];
  readonly constraints?: {
    readonly hierarchy?: readonly string[];
    readonly docRef?: readonly string[];
  };
}

export interface ForeignKeyPathSchema {
  readonly kind: "FOREIGN_KEY_PATH";
  readonly type: string[];
}

export interface ForeignKeyStringSchema {
  readonly kind: "FOREIGN_KEY_STRING";
  readonly allowedTypes?: string[];
}

export interface PrimaryKeySchema {
  readonly kind: "PRIMARY_KEY";
  readonly skipRekey: boolean;
  readonly type: string;
}

export interface PrimaryKeyTableSchema {
  readonly kind: "PRIMARY_KEY_TABLE";
  readonly type: string;
  readonly skipRekey: boolean;
  readonly valueType: Schema;
}

export interface ForeignKeyTableSchema {
  readonly kind: "FOREIGN_KEY_TABLE";
  readonly type: string | string[];
  readonly valueType: Schema;
}

export interface ForeignKeyHashTableSchema {
  readonly kind: "FOREIGN_KEY_HASH_TABLE";
  readonly type: string | string[];
  readonly valueType: Schema;
}

export interface ForeignKeyHashJoinTableSchema {
  readonly kind: "FOREIGN_KEY_HASH_JOIN_TABLE";
  readonly type: string | string[];
  readonly valueType: Schema;
}

export interface TupleSchema {
  readonly kind: "TUPLE";
  readonly tupleTypes: Schema[];
  readonly name?: string;
}

export interface ObjectPropertySchema {
  readonly kind: "OBJECT_PROPERTY";
  readonly name: string;
  readonly schema: Schema;
}

export interface ManualSchema {
  readonly kind: "MANUAL";
  readonly name: string;
}

export interface OmitSchema {
  readonly kind: "OMIT";
  readonly valueType: Schema;
  readonly fields: LiteralSchema | UnionSchema;
}

export interface PickSchema {
  readonly kind: "PICK";
  readonly valueType: Schema;
  readonly fields: LiteralSchema | UnionSchema;
}

export interface TypeIdentifierSchema {
  readonly kind: "IDENTIFIER";
  readonly name: string;
}

export interface MappedObjectSchema {
  readonly kind: "MAPPED_OBJECT";
  readonly indexType: Schema;
  readonly valueType: Schema;
  readonly hasQuestionToken: boolean;
  readonly name?: string;
}

export interface AnySchema {
  readonly kind: "ANY";
}

export interface StringSchema {
  readonly kind: "STRING";
}

export interface NumberSchema {
  readonly kind: "NUMBER";
}

export interface BooleanSchema {
  readonly kind: "BOOLEAN";
}

export interface NullishSchema {
  readonly kind: "NULLISH";
  readonly subType: "undefined" | "null";
}

export interface ArraySchema {
  readonly kind: "ARRAY";
  readonly arrayType: Schema;
}

export interface InterfaceSchema<Fields extends Record<string, Schema> = Record<string, Schema>> {
  readonly kind: "INTERFACE";
  readonly fields: Fields;
  readonly typeArgs?: string[];
  readonly name?: string;
}

export interface TypedArraySchema {
  readonly kind: "TYPED_ARRAY";
  readonly name:
    | "Float32Array"
    | "Float64Array"
    | "Int8Array"
    | "Int16Array"
    | "Int32Array"
    | "Uint8Array"
    | "Uint8ClampedArray"
    | "Uint16Array"
    | "Uint32Array";
}

export interface NominalNumberSchema {
  readonly kind: "NOMINAL_NUMBER";
  readonly name:
    | "WorldInches"
    | "WorldFeet"
    | "LevelInches"
    | "LevelFeet"
    | "InchesSquared"
    | "InchesCubed"
    | "FeetSquared"
    | "FeetCubed"
    | "YardSquared"
    | "YardsCubed"
    | "SheetInches"
    | "SheetPoints"
    | "SheetPixels"
    | "OneHundredthMillimeters"
    | "DotsPerInch"
    | "PixelsPerInch"
    | "Slope"
    | "UnsignedNumber"
    | "UntypedNumber"
    | "Percent";
}

export interface LiteralSchema {
  readonly kind: "LITERAL";
  readonly value: string | number | boolean;
}

export interface EnumSchema {
  readonly kind: "ENUM";
  readonly name: string;
  readonly members: Record<string, string | number | boolean>;
}

export interface TypeParameterSchema {
  readonly kind: "TYPE_PARAMETER";
  readonly name: string;
}

export interface TypeReferenceSchema {
  readonly kind: "TYPE_REFERENCE";
  readonly targetType: Schema;
  readonly typeArgs: Table<Schema>;
}

export interface ObjectSchema {
  readonly kind: "OBJECT";
  readonly valueType: Schema;
  readonly name?: string;
}

export interface UnionSchema<Variants extends Schema[] = Schema[]> {
  readonly kind: "UNION";
  readonly unionTypes: Variants;
  readonly name?: string;
}

export interface IntersectionSchema {
  readonly kind: "INTERSECTION";
  readonly intersectionTypes: Schema[];
  readonly name?: string;
}

export interface PartialSchema {
  readonly kind: "PARTIAL";
  readonly valueType: Schema;
}

export const any: AnySchema = { kind: "ANY" };
export const str: StringSchema = { kind: "STRING" };
export const num: NumberSchema = { kind: "NUMBER" };
export const bool: BooleanSchema = { kind: "BOOLEAN" };
export const undef: NullishSchema = { kind: "NULLISH", subType: "undefined" };
export const nul: NullishSchema = { kind: "NULLISH", subType: "null" };

export function id(name: string): TypeIdentifierSchema {
  return { kind: "IDENTIFIER", name };
}

export function lit(value: LiteralSchema["value"]): LiteralSchema {
  return { kind: "LITERAL", value };
}

export function arr(arrayType: Schema): ArraySchema {
  return { kind: "ARRAY", arrayType };
}

export function tarr(name: TypedArraySchema["name"]): TypedArraySchema {
  return { kind: "TYPED_ARRAY", name };
}

export function interf<Fields extends Record<string, Schema>>(
  fields: Fields,
  name?: string,
  typeParams?: string[],
): InterfaceSchema<Fields> {
  return { kind: "INTERFACE", fields, name, typeArgs: typeParams };
}

export function enu(members: EnumSchema["members"], name: string): EnumSchema {
  return { kind: "ENUM", members, name };
}

export function union<Variants extends Schema[]>(unionTypes: Variants, name?: string): UnionSchema<Variants> {
  return { kind: "UNION", unionTypes, name };
}

export function obj(valueType: Schema, name?: string): ObjectSchema {
  return { kind: "OBJECT", valueType, name };
}

export function mapobj(
  indexType: Schema,
  valueType: Schema,
  hasQuestionToken: boolean,
  name?: string,
): MappedObjectSchema {
  return { kind: "MAPPED_OBJECT", indexType, valueType, hasQuestionToken, name };
}

export function opt<T extends Schema>(valueType: T): UnionSchema<(T | NullishSchema)[]> {
  return union([undef, valueType]);
}

export function partial(valueType: Schema): PartialSchema {
  return { kind: "PARTIAL", valueType };
}

export function inters(intersectionTypes: Schema[], name?: string): IntersectionSchema {
  return { kind: "INTERSECTION", intersectionTypes, name };
}

export function pick(valueType: Schema, fields: PickSchema["fields"]): PickSchema {
  return { kind: "PICK", valueType, fields };
}

export function omit(valueType: Schema, fields: LiteralSchema | UnionSchema): OmitSchema {
  return { kind: "OMIT", valueType, fields };
}

export function manual(name: string): ManualSchema {
  return { kind: "MANUAL", name };
}

export function tuple(tupleTypes: Schema[], name?: string): TupleSchema {
  return { kind: "TUPLE", tupleTypes, name };
}

export function objectProp(name: string | number, schema: Schema): ObjectPropertySchema {
  return { kind: "OBJECT_PROPERTY", name: typeof name === "number" ? name.toString() : name, schema };
}

export function typeParam(name: string): TypeParameterSchema {
  return {
    kind: "TYPE_PARAMETER",
    name,
  };
}

export function ref(targetType: Schema, typeArgs: Table<Schema>): TypeReferenceSchema {
  return {
    kind: "TYPE_REFERENCE",
    targetType,
    typeArgs,
  };
}

export function fk(
  type: string | string[],
  constraints?: {
    readonly hierarchy?: readonly string[];
    readonly docRef?: readonly string[];
  },
): ForeignKeySchema {
  return {
    kind: "FOREIGN_KEY",
    type: Array.isArray(type) ? type : [type],
    ...(constraints && { constraints }),
  };
}

export function fkStrOf(allowedTypes: string[]): ForeignKeyStringSchema {
  return {
    kind: "FOREIGN_KEY_STRING",
    allowedTypes,
  };
}

export function fkPathOf(type: string | string[]): ForeignKeyPathSchema {
  return {
    kind: "FOREIGN_KEY_PATH",
    type: Array.isArray(type) ? type : [type],
  };
}

export function pk(type: string, skipRekey: boolean = false): PrimaryKeySchema {
  return {
    kind: "PRIMARY_KEY",
    type,
    skipRekey,
  };
}

export function pkTable(type: string, valueType: Schema, skipRekey: boolean = false): PrimaryKeyTableSchema {
  return {
    kind: "PRIMARY_KEY_TABLE",
    type,
    valueType,
    skipRekey,
  };
}

export function fkTable(type: string, valueType: Schema): ForeignKeyTableSchema;
export function fkTable(types: string[], valueType: Schema): ForeignKeyTableSchema;
export function fkTable(type: string | string[], valueType: Schema): ForeignKeyTableSchema {
  return {
    kind: "FOREIGN_KEY_TABLE",
    type,
    valueType,
  };
}

export function fkHashTable(type: string, valueType: Schema): ForeignKeyHashTableSchema;
export function fkHashTable(types: string[], valueType: Schema): ForeignKeyHashTableSchema;
export function fkHashTable(type: string | string[], valueType: Schema): ForeignKeyHashTableSchema {
  return { kind: "FOREIGN_KEY_HASH_TABLE", type, valueType } as ForeignKeyHashTableSchema;
}

export function fkHashJoinTable(type: string, valueType: Schema): ForeignKeyHashJoinTableSchema;
export function fkHashJoinTable(types: string[], valueType: Schema): ForeignKeyHashJoinTableSchema;
export function fkHashJoinTable(type: string | string[], valueType: Schema): ForeignKeyHashJoinTableSchema {
  return { kind: "FOREIGN_KEY_HASH_JOIN_TABLE", type, valueType } as ForeignKeyHashJoinTableSchema;
}

export function nom(name: NominalNumberSchema["name"]): NominalNumberSchema {
  return { kind: "NOMINAL_NUMBER", name };
}

export type SchemaMetadata = {
  readonly date: string;
  readonly comment: string;
};

export type MovedSchema = {
  readonly kind: "MOVED";
  readonly schema: Schema;
  readonly movedTo: string;
  readonly metadata: SchemaMetadata;
};

export interface DeletedSchema {
  readonly kind: "DELETED";
  readonly schema: Schema;
  readonly metadata: SchemaMetadata;
}

export function moved(schema: Schema, movedTo: string, metadata: SchemaMetadata): MovedSchema {
  return { kind: "MOVED", schema, movedTo, metadata };
}

export function deleted(schema: Schema, metadata: SchemaMetadata): DeletedSchema {
  return { kind: "DELETED", schema, metadata };
}

export type DeprecatedSchema = {
  readonly kind: "DEPRECATED";
  readonly schema: Schema;
  readonly metadata: SchemaMetadata;
};

export function deprecated(schema: Schema, metadata: SchemaMetadata): DeprecatedSchema {
  return { kind: "DEPRECATED", schema, metadata };
}

export interface DescribedSchema {
  readonly kind: "DESCRIBED";
  readonly schema: Schema;
  readonly description: string;
}

export function described(schema: Schema, description: string): DescribedSchema {
  return { kind: "DESCRIBED", schema, description };
}
